import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ThumbsUp, ChevronDown, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/lib/queries";

export function NotAnIssueButton({ flag, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const save = async () => {
    try {
      await api.post(`/flags/${flag.id}/not-an-issue`, { note });
      toast.success("Got it. The scribe won't raise this again.");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1 text-[11px] text-[#A8A090] hover:text-[#10B981]" data-testid={`not-an-issue-${flag.id}`}><ThumbsUp className="h-3.5 w-3.5" />Not an issue</button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-2" data-testid="not-an-issue-popover">
        <p className="text-sm text-[#E8DFD1]">Mark as not an issue? The AI review will never raise this point again.</p>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why? (optional, e.g. 'intentional mystery')" data-testid="not-an-issue-note" />
        <div className="flex justify-end"><Button size="sm" onClick={save} data-testid="not-an-issue-confirm">Not an issue</Button></div>
      </PopoverContent>
    </Popover>
  );
}

export function DismissedFlags({ ent, canEdit }) {
  const invalidate = useInvalidate();
  const [open, setOpen] = useState(false);
  const { data: items = [] } = useQuery({ queryKey: ["dismissed-flags", ent.id], queryFn: () => api.get(`/entities/${ent.id}/dismissed-flags`).then((r) => r.data), enabled: canEdit });
  if (!canEdit || !items.length) return null;
  const restore = async (d) => {
    await api.delete(`/dismissed-flags/${d.id}`);
    invalidate(["dismissed-flags", ent.id]);
    toast.success("The scribe may raise this again on the next review");
  };
  return (
    <div className="mt-3" data-testid="dismissed-flags">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F3EBDD]" data-testid="dismissed-flags-toggle">
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />{items.length} point{items.length > 1 ? "s" : ""} marked not an issue
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          {items.map((d) => (
            <div key={d.id} className="flex items-start justify-between gap-3 rounded border border-[#2D3442] px-3 py-2 text-xs text-[#A8A090]" data-testid={`dismissed-flag-${d.id}`}>
              <div><span className="uppercase tracking-widest text-[10px]">{d.type.replace("_", " ")}</span> · {d.message}{d.note && <span className="italic text-[#E8DFD1]"> (“{d.note}”)</span>}</div>
              <button onClick={() => restore(d)} className="flex shrink-0 items-center gap-1 hover:text-[#F59E0B]" title="Allow the AI to raise this again" data-testid={`restore-dismissed-${d.id}`}><RotateCcw className="h-3.5 w-3.5" />Allow again</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
